import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import { PageHeader, LoadingState, DataTable } from '../components/AdminPageUI';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Avatar, Box, Button, Card, CardContent, Checkbox, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, IconButton, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../utils/Api';

const audienceOptions = [
  { value: 'all', label: 'All users' },
  { value: 'admin', label: 'Admins' },
  { value: 'teacher', label: 'Teachers' },
  { value: 'parent', label: 'Parents' },
  { value: 'student', label: 'Students' },
];

const platformOptions = [
  { value: 'all', label: 'All platforms' },
  { value: 'admin-dashboard', label: 'Admin dashboard' },
  { value: 'teacher-portal', label: 'Teacher portal' },
  { value: 'mobile', label: 'Mobile app' },
];

const emptyForm = {
  title: '',
  summary: '',
  details: '',
  version: '',
  audiences: ['all'],
  platforms: ['all'],
  startsAt: new Date().toISOString().slice(0, 16),
  endsAt: '',
  isActive: true,
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toggleListValue = (values, value) => {
  if (value === 'all') return ['all'];

  const withoutAll = values.filter((item) => item !== 'all');
  const next = withoutAll.includes(value)
    ? withoutAll.filter((item) => item !== value)
    : [...withoutAll, value];

  return next.length ? next : ['all'];
};

export default function ReleaseNotesPage({ superAdminMode = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notes, setNotes] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const inactiveCount = useMemo(
    () => Math.max(0, Number(summary.total || 0) - Number(summary.active || 0)),
    [summary],
  );

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/release-notes', { params: { limit: 100 } });
      setNotes(res.data?.notes || []);
      setSummary(res.data?.summary || { total: 0, active: 0 });
    } catch (err) {
      console.error('Failed to load release notes:', err);
      const message = err.response?.data?.message || 'Failed to load release notes.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const openCreateDialog = () => {
    setEditingNote(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (note) => {
    setEditingNote(note);
    setForm({
      title: note.title || '',
      summary: note.summary || '',
      details: note.details || '',
      version: note.version || '',
      audiences: note.audiences?.length ? note.audiences : ['all'],
      platforms: note.platforms?.length ? note.platforms : ['all'],
      startsAt: toDateTimeInput(note.startsAt) || emptyForm.startsAt,
      endsAt: toDateTimeInput(note.endsAt),
      isActive: note.isActive !== false,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingNote(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.warning('Title is required.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        title: form.title.trim(),
        summary: form.summary.trim(),
        details: form.details.trim(),
        version: form.version.trim(),
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : undefined,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };

      if (editingNote?._id) {
        await api.put(`/release-notes/${editingNote._id}`, payload);
        toast.success("What's New update saved.");
      } else {
        await api.post('/release-notes', payload);
        toast.success("What's New update published.");
      }

      closeDialog();
      loadNotes();
    } catch (err) {
      console.error('Failed to save release note:', err);
      toast.error(err.response?.data?.message || 'Failed to save release note.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note) => {
    const confirmed = window.confirm(`Delete "${note.title}"?`);
    if (!confirmed) return;

    try {
      await api.delete(`/release-notes/${note._id}`);
      toast.success('Release note deleted.');
      loadNotes();
    } catch (err) {
      console.error('Failed to delete release note:', err);
      toast.error(err.response?.data?.message || 'Failed to delete release note.');
    }
  };

  const renderOptionGroup = (label, options, field) => (
    <Box>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
        {label}
      </Typography>
      <FormGroup row={!isMobile}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={form[field].includes(option.value)}
                onChange={() =>
                  setForm((prev) => ({
                    ...prev,
                    [field]: toggleListValue(prev[field], option.value),
                  }))
                }
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      <PageHeader title={<>{"What's New"}</>} description={<>Publish update notes for admins, teachers, parents, and students.</>} actions={<>
        {superAdminMode && <Button variant="outlined" onClick={() => navigate('/super-admin/dashboard')} fullWidth={isMobile}>
                    Control Center
                  </Button>}
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadNotes} disabled={loading} fullWidth={isMobile}>
                  Refresh
                </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} fullWidth={isMobile}>
                  New Update
                </Button>
      </>}></PageHeader>
      <SummaryStrip container spacing={2.5} sx={{ mb: 3 }}>
        {[
          { label: 'Total Updates', value: summary.total || 0, color: theme.palette.primary.main },
          { label: 'Active Updates', value: summary.active || 0, color: theme.palette.success.main },
          { label: 'Inactive Updates', value: inactiveCount, color: theme.palette.warning.main },
        ].map((item) => (
          <Grid size={{ xs: 12, md: 4 }}    key={item.label}>
            <SummaryMetric
              sx={{
                border: `1px solid ${alpha(item.color, 0.35)}`,
                boxShadow: 1,
                height: '100%',
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={700}>
                      {item.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={900}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(item.color, 0.12), color: item.color }}>
                    <CampaignIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </SummaryMetric>
          </Grid>
        ))}
      </SummaryStrip>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      

      <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: isMobile ? 1 : 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" fontWeight={800}>
              Published Updates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Users see active updates once until they tap Got it.
            </Typography>
          </Box>

          {loading ? (
            <LoadingState />
          ) : (
            <DataTable sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Update</TableCell>
                    <TableCell>Audience</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Starts</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((note) => (
                    <TableRow key={note._id} hover>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography fontWeight={800}>{note.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {note.summary || 'No summary'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(note.audiences || []).map((item) => <Chip key={item} size="small" label={item} />)}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(note.platforms || []).map((item) => <Chip key={item} size="small" label={item} />)}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={note.isActive ? 'success' : 'default'}
                          label={note.isActive ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(note.startsAt)}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEditDialog(note)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(note)} size="small" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!notes.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <Typography fontWeight={800}>No updates yet</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Create the first What's New update for your users.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle fontWeight={900}>
          {editingNote ? 'Edit Update' : 'New Update'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}   >
                <TextField
                  fullWidth
                  label="Title"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}   >
                <TextField
                  fullWidth
                  label="Version"
                  placeholder="1.0.26"
                  value={form.version}
                  onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Short summary"
              value={form.summary}
              onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
              inputProps={{ maxLength: 280 }}
              helperText={`${form.summary.length}/280`}
            />

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Details"
              value={form.details}
              onChange={(e) => setForm((prev) => ({ ...prev, details: e.target.value }))}
            />

            {renderOptionGroup('Audience', audienceOptions, 'audiences')}
            {renderOptionGroup('Where it should show', platformOptions, 'platforms')}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}   >
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Starts"
                  InputLabelProps={{ shrink: true }}
                  value={form.startsAt}
                  onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}   >
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Ends"
                  InputLabelProps={{ shrink: true }}
                  value={form.endsAt}
                  onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
